import type { SavedView, SectionViews } from "@gettactic/api";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { logError } from "../../../../helpers/src/errors/logError";
import { useTacticHooks } from "../../providers/TacticHooksProvider";
import {
  getAreaViews,
  removeSavedView,
  type SavedViewValidationError,
  savedViewErrorMessage,
  upsertSavedView,
  validateSavedView
} from "./savedViewsMap";
import { useCoreUserSettings } from "./useCoreUserSettings";
import { useInvalidateCoreUserSettings } from "./useInvalidateCoreUserSettings";

/**
 * Thrown when a view breaks one of the backend's limits. The API answers every
 * violation with the same `invalid_view` code, so we detect the cause here and
 * carry a message the UI can show as-is.
 */
export class SavedViewError extends Error {
  readonly reason: SavedViewValidationError;

  constructor(reason: SavedViewValidationError) {
    super(savedViewErrorMessage(reason));
    this.name = "SavedViewError";
    this.reason = reason;
  }
}

/**
 * The saved views stored under one section/area of the user settings
 * (e.g. `tickets` / `dashboard`).
 *
 * `filters` stays untyped here on purpose: it is opaque JSON the backend never
 * validates, so each screen narrows it with its own parser rather than trusting
 * a type assertion made at read time.
 */
export const useSavedViews = (section: string, area: string) => {
  const settings = useCoreUserSettings();

  const views = useMemo(
    () => getAreaViews(settings.data?.settings.views, section, area),
    [settings.data, section, area]
  );

  const getById = useCallback(
    (id: string | null | undefined): SavedView | null =>
      id ? (views.find((v) => v.id === id) ?? null) : null,
    [views]
  );

  return {
    views,
    getById,
    isLoading: settings.isLoading,
    /**
     * True while the settings request is in flight, background refetches of a
     * stale cache included. A `?view=` deep link has to wait for this, not just
     * for `isLoading`: a view saved in another tab is absent from the cached
     * payload, and acting on it would read as "this view no longer exists".
     */
    isFetching: settings.isFetching
  };
};

/**
 * Create / update / delete saved views.
 *
 * Every mutation is a read-modify-write of the *entire* `views` object, because
 * the backend replaces rather than merges that field. Two safeguards keep that
 * from losing data: each mutation re-reads the settings from the network right
 * before writing (the query cache is 5 minutes stale), and `isMutating` lets
 * the UI serialize its own writes. Concurrent edits from another tab can still
 * race — closing that needs a version check on the API.
 */
export const useSavedViewMutations = (section: string, area: string) => {
  const { apiClient } = useTacticHooks();
  const invalidateUserSettings = useInvalidateCoreUserSettings();

  const writeViews = useCallback(
    async (
      apply: (views: SectionViews) => SectionViews,
      validate?: (views: SectionViews) => SavedViewValidationError | null
    ) => {
      const current = await apiClient.userSettings.getCoreUserSettings();
      if (!current.ok) throw current;
      const currentViews = current.result?.settings.views ?? {};

      const invalid = validate?.(currentViews);
      if (invalid) throw new SavedViewError(invalid);

      const res = await apiClient.userSettings.updateViews(apply(currentViews));
      if (!res.ok) throw res;
      await invalidateUserSettings();
    },
    [apiClient, invalidateUserSettings]
  );

  const saveView = useMutation({
    mutationFn: async ({
      view,
      isNew
    }: {
      view: SavedView;
      isNew: boolean;
    }) => {
      const trimmed: SavedView = { ...view, name: view.name.trim() };
      try {
        await writeViews(
          (views) => upsertSavedView(views, section, area, trimmed),
          (views) => validateSavedView(views, section, area, trimmed, isNew)
        );
      } catch (error) {
        // A SavedViewError is an expected, user-correctable outcome, not a fault.
        if (!(error instanceof SavedViewError)) {
          logError("Error saving view", error);
        }
        throw error;
      }
    }
  });

  const removeView = useMutation({
    mutationFn: async (viewId: string) => {
      try {
        await writeViews((views) =>
          removeSavedView(views, section, area, viewId)
        );
      } catch (error) {
        logError("Error deleting view", error);
        throw error;
      }
    }
  });

  return {
    saveView,
    removeView,
    /** True while any view write is in flight; use it to serialize UI actions. */
    isMutating: saveView.isPending || removeView.isPending
  };
};
